/**
 * Blog post: Goethe-Zertifikat C1 – Neues Format, Précis & Strategien.
 * Content: new format, academic précis, Goethe vs. telc C1, Redemittel link.
 */
import React from 'react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import { Link } from 'react-router-dom';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

export default function GoetheC1ExamGuidePost() {
  const lang = useLangParam();
  const blogBase = `/${lang}/blog`;

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        <strong>Alles über das neue Format und wie du die C1 Prüfung meisterst – von logami.app.</strong>
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-10">
        Die <strong>Goethe C1</strong> Prüfung ist der ultimative Nachweis für <strong>High-Level Academic</strong> Deutschkenntnisse. Mit dem <strong>neuen Format</strong> hat das Goethe-Institut die Prüfung modularer und moderner gestaltet. Um das <strong>Goethe-Zertifikat C1</strong> zu erhalten, musst du nun in vier Modulen glänzen, die du auch einzeln <strong>meistern</strong> kannst.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Das neue Format: Was hat sich geändert?
      </h2>
      <div className="my-6 p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
          Seit der Umstellung ist die <strong>Goethe C1 Prüfung</strong> modular aufgebaut. Das bedeutet für dich:
        </p>
        <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed ml-2">
          <li><strong>Flexibilität:</strong> Bestehe <strong>Listening</strong>, <strong>Reading</strong>, <strong>Writing</strong> und <strong>Speaking</strong> unabhängig voneinander.</li>
          <li><strong>Präzision:</strong> Der Fokus liegt stärker auf akademischer Authentizität.</li>
          <li><strong>Zeitmanagement:</strong> Die Aufgaben im <strong>Reading &amp; Writing</strong>-Teil sind kompakter, verlangen aber eine höhere kognitive Leistung.</li>
        </ul>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Focus: High-Level Academic Précis
      </h2>
      <div className="my-6 p-5 rounded-2xl border-l-4 border-amber-500 bg-amber-50/80">
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
          Die größte Hürde im <strong>Writing</strong>-Teil ist oft die Zusammenfassung von Texten (Précis). Hier wird nicht nur Grammatik bewertet, sondern deine Fähigkeit, komplexe Informationen zu verdichten.
        </p>
        <ul className="list-disc list-inside space-y-2 text-base md:text-lg text-black/70 leading-relaxed ml-2">
          <li>Nutze gehobene Strukturen wie Nominalisierung und Passiv.</li>
          <li>Vermeide direkte Zitate; zeige deine <strong>German Language Proficiency</strong> durch Paraphrasierung.</li>
          <li>Strukturiere deinen Text mit logischen Konnektoren.</li>
        </ul>
      </div>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Goethe vs. telc C1: Wo liegen die Unterschiede?
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mb-10">
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
          <h3 className="font-space text-lg font-bold text-black mb-3">Goethe C1</h3>
          <p className="text-base text-black/70 leading-relaxed">
            Sehr starker akademischer Fokus. Ideal für Studenten, die eine <strong>University</strong>-Laufbahn anstreben. Die Module sind strikt getrennt.
          </p>
        </div>
        <div className="p-4 sm:p-5 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
          <h3 className="font-space text-lg font-bold text-black mb-3">telc C1 (Hochschule)</h3>
          <p className="text-base text-black/70 leading-relaxed">
            Oft als etwas &quot;praxisnäher&quot; empfunden, aber im <strong>Listening &amp; Reading</strong>-Teil extrem anspruchsvoll. Die Bewertung im <strong>Speaking</strong>-Teil unterscheidet sich leicht im Format.
          </p>
        </div>
      </div>

      <div className="my-10 p-6 rounded-2xl border-2 border-black/10 bg-black/[0.02]">
        <h2 className="font-space text-xl font-bold text-black mb-3">
          Kostenlose Redemittel &amp; Practice Materials
        </h2>
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
          Bereite dich <strong>Schritt für Schritt</strong> vor. Wir bieten dir <strong>kostenlose</strong> Downloads für deine <strong>Exam Preparation</strong>.
        </p>
        <Link
          to={`${blogBase}/goethe-c1-redemittel-schreiben`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-black hover:bg-black/90 text-white font-space font-bold rounded-xl border-2 border-black shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Jetzt C1 Redemittel (Free PDF) sichern
        </Link>
        <p className="mt-3 text-sm text-black/60">
          Nutze unsere <strong>Mock Examination</strong>-Simulatoren für ein realistisches <strong>Exam Training</strong>.
        </p>
      </div>
      <SprechenSamplesCallout provider="goethe" level="c1" />

      <div className="mt-10 p-6 rounded-2xl border-2 border-black/10 bg-[#1e293b] text-white">
        <h2 className="font-space text-xl font-bold text-white mb-3">
          Bist du bereit für das C1 Zertifikat?
        </h2>
        <p className="text-base md:text-lg text-white/90 leading-relaxed mb-4">
          Keine Sorge wegen der <strong>C1 Exam</strong>. Mit unseren <strong>Practice Tests</strong> und der Hilfe von <strong>Real German Teachers</strong> wirst du die Prüfung <strong>ace</strong>.
        </p>
        <Link
          to={`/${lang}/dashboard`}
          className="inline-flex items-center justify-center min-h-[48px] px-5 py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-space font-bold rounded-xl border-2 border-emerald-500 shadow-md transition-all hover:scale-[1.02] active:scale-[0.98]"
        >
          Jetzt kostenlos Mock Exam starten
        </Link>
      </div>

      <p className="mt-10 pt-6 border-t border-black/10 text-sm text-black/50">
        Keywords: Goethe C1, neue Medien, Prüfung telc C1, University, Prüfungstraining, Modelltest, Deutsch C1.
      </p>
      <RelatedResources provider="goethe" level="c1" variant="exam-guide" />

    </article>
  );
}
